import { Grid, GridColumn, GridRow, LazyLoader } from '@bp/ui-components';
import { BpCard } from 'components/BpCard/BpCard';
import { BpPage } from 'components/BpPage/BpPage';
import { ClassbookEvent } from 'components/Classbook/components/ClassbookEvents/ClassbookEvent';
import { Timetable } from 'components/Timetable/Timetable';
import dayjs, { Dayjs } from 'dayjs';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { Suspense, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWindowDimensions } from 'utils/dimensions';
import { usePermissionChecker } from '../../hooks/usePermissionChecker';
import { useStackHandler } from '../../hooks/useStackHandler';

export const ClassbookPage = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const handler = useStackHandler();

  const { isTablet, isPhone } = useWindowDimensions();

  const isMobile = isTablet || isPhone;
  const organization = pimAuthClaims.getOrganization();

  const permissions = usePermissionChecker();

  const [eventUuid, setEventUuid] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<Dayjs>(dayjs());

  const isWholeDay = useCallback(
    ({ start, end }: { start: Dayjs; end: Dayjs }) => {
      // if start and end cover the whole selectedDay (00:00 to 23:59)
      return start.isSameOrBefore(selectedDay.startOf('day')) && end.isSameOrAfter(selectedDay.endOf('day'));
    },
    [selectedDay],
  );

  function handleEventClick(uuid: string, title: string) {
    if (isMobile) {
      handler.push({
        id: `classbook-event-${uuid}`,
        title: title,
        group: 'classbook' as const,
        component: (
          <Suspense fallback={<LazyLoader embedded transparent size='xxl' forceHeight='100vh' />}>
            <ClassbookEvent key={uuid} eventUuid={uuid} isWholeDay={isWholeDay} />
          </Suspense>
        ),
        closeable: true,
      });
    } else {
      setEventUuid((current) => {
        return current === uuid ? null : uuid;
      });
    }
  }

  function handleDateChange() {
    setEventUuid(null);
    if (isMobile && handler.getStackSize() > 0) {
      handler.clear();
    }
  }

  return (
    <BpPage title={t('classbook.title', { count: 1 })} isForbidden={!permissions?.canViewClassbook(organization)}>
      <Suspense fallback={<LazyLoader embedded transparent size='xxl' forceHeight='60vh' />}>
        <Grid>
          <GridRow mobileGap='var(--grid-column-gap)'>
            <GridColumn width={isMobile ? 12 : 4}>
              <BpCard>
                <Timetable
                  context={'classbook'}
                  showDaySelect
                  showLegend
                  hideTodayHighlight
                  days={1}
                  onEventClick={({ eventUuid, eventTitle }) => handleEventClick(eventUuid, eventTitle ?? '')}
                  onDateChanged={() => handleDateChange()}
                  selectedDay={selectedDay}
                  setSelectedDay={setSelectedDay}
                  selectedEntryUuid={eventUuid ?? undefined}
                  eventColorSource={'class'}
                />
              </BpCard>
            </GridColumn>
            {!isMobile && (
              <GridColumn width={8}>
                <Suspense fallback={<LazyLoader embedded transparent size='xxl' forceHeight='40vh' />}>
                  <ClassbookEvent key={eventUuid} eventUuid={eventUuid} isWholeDay={isWholeDay} />
                </Suspense>
              </GridColumn>
            )}
          </GridRow>
        </Grid>
      </Suspense>
    </BpPage>
  );
};
